import { isBrowser } from './browser';
import { COOKIE_NAMES, SESSION_STORAGE_KEYS } from '../constants/localStorage';
import type { ConsentState } from '../constants/consent';
import { CONSENT_TTL_SECONDS, CONSENT_TTL_MS } from '../constants/consent';
import {
  REGION_COOKIE_MAX_AGE_SECONDS,
  type RegionInfo,
} from '../constants/region';
import { isValidRegion, toStore } from './region';
import type { CookieOptions } from '~/types/cookies';

const DEFAULT_OPTIONS: CookieOptions = {
  maxAge: CONSENT_TTL_SECONDS,
  path: '/',
  sameSite: 'Lax',
  secure: true,
};

function isConsentState(value: unknown): value is ConsentState {
  return (
    typeof value === 'object' &&
    value !== null &&
    'analytics' in value &&
    'functionality' in value &&
    'marketing' in value &&
    'ts' in value &&
    typeof (value as ConsentState).analytics === 'boolean' &&
    typeof (value as ConsentState).functionality === 'boolean' &&
    typeof (value as ConsentState).marketing === 'boolean' &&
    typeof (value as ConsentState).ts === 'string'
  );
}

export class CookieService {
  static set(name: string, value: string, options: CookieOptions = {}) {
    if (!isBrowser) return;

    const opts = { ...DEFAULT_OPTIONS, ...options };
    if (opts.sameSite === 'None') opts.secure = true;

    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (opts.maxAge) cookie += `; max-age=${opts.maxAge}`;
    if (opts.path) cookie += `; path=${opts.path}`;
    if (opts.sameSite) cookie += `; SameSite=${opts.sameSite}`;
    if (opts.secure) cookie += '; Secure';

    document.cookie = cookie;
  }

  static get(name: string): string | null {
    if (!isBrowser) return null;

    const cookies = document.cookie.split('; ');
    const cookie = cookies.find((c) =>
      c.startsWith(`${encodeURIComponent(name)}=`),
    );

    if (!cookie) return null;

    return decodeURIComponent(cookie.split('=')[1]);
  }

  /**
   * `domain` is only needed for cookies we did not set ourselves: a cookie is removable
   * only by a delete whose domain matches how it was created, and a third-party script
   * picks its own scope. Since that scope is not ours to know, callers clear both ways.
   *
   * Deliberately not mirrored onto `set()` / `CookieOptions`: every cookie this app
   * writes is host-only, so the option would be dead weight there.
   */
  static remove(name: string, path = '/', domain?: string) {
    if (!isBrowser) return;

    const base = `${encodeURIComponent(name)}=; max-age=0; path=${path}`;

    document.cookie = base;
    if (domain) document.cookie = `${base}; domain=${domain}`;
  }

  static getConsent(): ConsentState | null {
    if (!isBrowser) return null;

    const raw = this.get(COOKIE_NAMES.cookieConsent);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as unknown;
      return isConsentState(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  static setConsent(state: ConsentState) {
    if (!isBrowser) return;

    const payload: ConsentState = {
      ...state,
      ts: state.ts || new Date().toISOString(),
    };

    this.set(COOKIE_NAMES.cookieConsent, JSON.stringify(payload), {
      maxAge: CONSENT_TTL_SECONDS,
    });
  }

  static isConsentExpired(state: ConsentState): boolean {
    try {
      const tsMs = new Date(state.ts).getTime();
      return Number.isNaN(tsMs) || tsMs + CONSENT_TTL_MS < Date.now();
    } catch {
      return true;
    }
  }

  static hasValidConsent(): boolean {
    const state = this.getConsent();
    return state !== null && !this.isConsentExpired(state);
  }

  static hasAnalyticsConsent(): boolean {
    const state = this.getConsent();
    return state !== null && !this.isConsentExpired(state) && state.analytics;
  }

  static hasFunctionalityConsent(): boolean {
    const state = this.getConsent();
    return (
      state !== null && !this.isConsentExpired(state) && state.functionality
    );
  }

  static hasMarketingConsent(): boolean {
    const state = this.getConsent();
    return state !== null && !this.isConsentExpired(state) && state.marketing;
  }

  static getRegionInfo(): RegionInfo | null {
    if (!isBrowser) return null;

    const cookieRegion = this.get(COOKIE_NAMES.region);
    const cookieCountry = this.get(COOKIE_NAMES.countryCode);

    if (isValidRegion(cookieRegion) && cookieCountry) {
      return toStore(cookieRegion, cookieCountry);
    }

    try {
      const sessionRegion = sessionStorage.getItem(SESSION_STORAGE_KEYS.region);
      const sessionCountry = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.countryCode,
      );

      if (isValidRegion(sessionRegion) && sessionCountry) {
        return toStore(sessionRegion, sessionCountry);
      }
    } catch {
      // sessionStorage unavailable
    }

    return null;
  }

  /** Remove persisted region cookies (e.g. when functionality consent is revoked). */
  static clearRegionCookies() {
    if (!isBrowser) return;
    this.remove(COOKIE_NAMES.region);
    this.remove(COOKIE_NAMES.countryCode);
  }

  static setRegionInfo(info: RegionInfo) {
    if (!isBrowser) return;

    const secure = window.location.protocol === 'https:';

    if (this.hasFunctionalityConsent()) {
      this.set(COOKIE_NAMES.region, info.region, {
        maxAge: REGION_COOKIE_MAX_AGE_SECONDS,
        secure,
      });
      this.set(COOKIE_NAMES.countryCode, info.countryCode, {
        maxAge: REGION_COOKIE_MAX_AGE_SECONDS,
        secure,
      });
    } else {
      // Session cookie (no max-age) — sent to the server on every request so
      // loaders can read the correct region for pricing, but expires when the
      // browser session ends. Strictly necessary, no consent required.
      this.set(COOKIE_NAMES.region, info.region, { maxAge: 0, secure });
      this.set(COOKIE_NAMES.countryCode, info.countryCode, {
        maxAge: 0,
        secure,
      });
    }

    this.clearRegionSessionStorage();
  }

  static migrateRegionToCookie() {
    if (!isBrowser || !this.hasFunctionalityConsent()) return;

    const info = this.getRegionInfo();
    if (!info) return;

    const secure = window.location.protocol === 'https:';
    this.set(COOKIE_NAMES.region, info.region, {
      maxAge: REGION_COOKIE_MAX_AGE_SECONDS,
      secure,
    });
    this.set(COOKIE_NAMES.countryCode, info.countryCode, {
      maxAge: REGION_COOKIE_MAX_AGE_SECONDS,
      secure,
    });
    this.clearRegionSessionStorage();
  }

  private static clearRegionSessionStorage() {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.region);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.countryCode);
    } catch {
      // sessionStorage unavailable
    }
  }
}
